import {
  DayHour,
  DayNumber,
  MapLike,
  MinuteNumber,
  MonthNumber,
  StateSnapshot,
  StateSnapshot as TypedStateSnapshot,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { getAccessorsBetween } from '../sharedFunctions'
import { NestedMap } from '../sharedTypes'
import { DehydratedPeriodized, ValidArrayDepthDefinitions } from './types'
import { enumerable } from '../../enumerableDecorator'
import { ModelCommon } from '../../../ModelCommon'
import {
  DEBUG_OVERRIDE,
  NOT_LAMBDA,
  TimeBucketType,
  fillWithXByArrayDef,
  timestampToYMDHMS,
  MeasureValueMapRange,
  ApportionMeasureMapResponse,
  apportionMeasureValuesMap,
} from '../../../../util'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const className = 'DimensionedPeriodizer'

export type DimensionedPeriodizerProps = {
  periodized?: NestedMap<number | null> | any[]
  arrayLengths?: number[]
  lastUpdatedTimestamp?: number
  keyMap?: MapLike<string>
  timeBucketDefinition?: TimeBucketType
}

export abstract class DimensionedPeriodizer<T extends MapLike<number | null>, DT extends ValidArrayDepthDefinitions> extends ModelCommon {
  constructor(ob?: ValidBusinessObject & DimensionedPeriodizerProps) {
    super(ob?.type ?? className, (({ sk, pk, type, id, name }: ValidBusinessObject) => ({ sk, pk, type, id, name }))(ob ?? ({} as ValidBusinessObject)))
    if (ob) {
      if (ob.periodized instanceof Array) {
        this.ingest(ob as DehydratedPeriodized)
      } else {
        this.periodized = ob.periodized
        this.arrayLengths = ob.arrayLengths as DT
        this.keyMap = ob.keyMap
        this.lastUpdatedTimestamp = ob.lastUpdatedTimestamp
      }
    }
  }
  periodized: NestedMap<number | null> | undefined
  arrayLengths?: DT
  lastUpdatedTimestamp: number | undefined
  keyMap?: MapLike<string>
  abstract timeBucketDefinition: TimeBucketType

  @enumerable(false)
  initialize = () => {
    const xFn = () => ({}) as T
    const periodized = this.arrayLengths ? fillWithXByArrayDef(this.arrayLengths, xFn)() : undefined
    periodized && this.ingest({ periodized, arrayLengths: this.arrayLengths, lastUpdatedTimestamp: undefined })
  }

  @enumerable(false)
  replaceTopLevelEntry = (periodizerIdx: number, xFn: () => T = () => ({}) as T) => {
    const emptyMapByArrayDef = (arrDef: number[]): NestedMap<number | null> =>
      arrDef.reduceRight(
        (a, c, i) => {
          const map: NestedMap<number | null> = {}
          for (let i = 0; i < c; i++) {
            map[i] = { ...a }
          }

          return map
        },
        xFn() as NestedMap<number | null>,
      )

    const [first, ...remaining] = this.arrayLengths ?? []
    const newRow = emptyMapByArrayDef(remaining) as NestedMap<number | null>
    this.periodized && (this.periodized[periodizerIdx] = newRow)
    throw new Error('not usable right now as need to deep clone accumulator or it will be same object in multiple places ')
  }

  @enumerable(false)
  getDimensions = (): number[] => {
    let dimStr: number[] = []
    let map: MapLike<any> | undefined = this.periodized
    do {
      const keys = map && Object.keys(map)
      keys && keys.length > 0 && keys.every((k) => typeof parseInt(k) === 'number') && dimStr.push(keys.length)
      map = map?.[0]
    } while (map)
    return dimStr
  }

  @enumerable(false)
  inferDimensions = (a: any[]) => {
    const inferredArrayLength: number[] = []
    let ax = a
    do {
      ax && inferredArrayLength.push(ax.length)
      ax = ax[0]
    } while (ax instanceof Array)
    return inferredArrayLength
  }

  @enumerable(false)
  ingest = ({ periodized: deydratedPeriodized, keyMap, lastUpdatedTimestamp, sk, pk, type, id, name }: DehydratedPeriodized) => {
    const rehydrateLevel = (levelArrMap: any[] | MapLike<number | null>): NestedMap<number | null> => {
      if (levelArrMap instanceof Array) {
        const arr = levelArrMap

        return Object.fromEntries(Object.entries(arr).map(([i, nextLevelArrMap]) => [i, rehydrateLevel(nextLevelArrMap)]))
      } else {
        const map = this.decodeMap(levelArrMap)
        return map
      }
    }
    this.keyMap = keyMap
    this.arrayLengths = deydratedPeriodized && (this.inferDimensions(deydratedPeriodized as any[]) as DT)
    this.periodized = deydratedPeriodized ? rehydrateLevel(deydratedPeriodized) : undefined
    this.lastUpdatedTimestamp = lastUpdatedTimestamp
    sk && (this.sk = sk)
    pk && (this.pk = pk)
    type && (this.type = type)
    id && (this.id = id)
    name && (this.name = name)
  }

  @enumerable(false)
  egest = (): DehydratedPeriodized | undefined =>
    this.periodized ? { ...this.dehydrate(), keyMap: this.keyMap, type: this.type, pk: this.pk, sk: this.sk, id: this.id, name: this.name } : undefined

  @enumerable(false)
  getShortKey = (k: string): string => {
    let shortCMVKey = k
    if (!this.keyMap) {
      this.keyMap = {}
    }
    if (this.keyMap?.[k]) {
      shortCMVKey = this.keyMap[k]
    } else {
      const keyMapEntriesNum = Object.keys(this.keyMap).length
      shortCMVKey = String.fromCharCode(keyMapEntriesNum + 65)
      this.keyMap[k] = shortCMVKey
    }
    return shortCMVKey
  }

  @enumerable(false)
  getOriginalKey = (shortKey: string): string => {
    let originalCMVKey = shortKey
    if (this.keyMap) {
      const matchedEntry = Object.entries(this.keyMap).find(([k, v]) => v === shortKey)
      if (matchedEntry) {
        const [ok, v] = matchedEntry
        originalCMVKey = ok
      }
    }
    return originalCMVKey
  }

  @enumerable(false)
  encodeMap = (map: MapLike<any>) => Object.fromEntries(Object.entries(map).map(([k, v]) => [this.getShortKey(k), v]))

  @enumerable(false)
  decodeMap = (map: MapLike<any>) => Object.fromEntries(Object.entries(map).map(([k, v]) => [this.getOriginalKey(k), v]))

  @enumerable(false)
  dehydrate = (): DehydratedPeriodized => {
    const dehydateLayer = (mapish: NestedMap<number | null>): any[] | MapLike<number | null> => {
      const keys = Object.keys(mapish)
      const isArrayLike: boolean = keys.length > 0 && keys.every((k) => !isNaN(parseInt(k)))
      if (isArrayLike) {
        const arrayish = mapish as MapLike<NestedMap<number | null>>
        return Object.values(arrayish).map((nextMapish) => dehydateLayer(nextMapish))
      } else {
        //debug && console.log('dehydratiing end map', mapish)
        return this.encodeMap(mapish as MapLike<number | null>)
      }
    }

    const dehydated: DehydratedPeriodized['periodized'] = this.periodized ? dehydateLayer(this.periodized) : undefined
    return { periodized: dehydated, keyMap: this.keyMap, arrayLengths: this.arrayLengths, lastUpdatedTimestamp: this.lastUpdatedTimestamp }
  }

  abstract isValidAccessor: (args: number[]) => args is DT

  getByIndex = (...args: number[]) =>
    args.reduce(
      (a, c) => {
        const accessed = a && typeof a === 'object' ? a[c] : a
        return accessed
      },
      this.periodized as number | null | NestedMap<number | null>,
    )

  setKeyByIndex = (mapKey: string, mapVal: number | null, ...args: number[]) => {
    if (this.getByIndex(...args)) {
      const sector = this.getByIndex(...args) as NestedMap<number | null>
      if (sector) {
        debug && console.log(`setting sector ${args} with val ${sector[mapKey]} to: ${mapVal}`)
        sector[mapKey] = mapVal
      }
    }
  }

  accumulateKeyByIndex = (mapKey: string, mapVal: number | null, ...args: number[]) => {
    if (this.getByIndex(...args)) {
      const sector = this.getByIndex(...args) as NestedMap<number | null>
      if (sector && !sector[mapKey]) {
        sector[mapKey] = 0
      }
      if (mapVal !== null && sector?.[mapKey] !== undefined && (sector[mapKey] === null || typeof sector[mapKey] === 'number')) {
        const currentVal = (sector[mapKey] as null | number) ?? 0
        debug && console.log(`accumulating sector ${args} with val ${sector[mapKey]} by ${mapVal} to ${mapVal + currentVal}`)
        sector[mapKey] = mapVal + currentVal
      } else {
        debug && console.log(`not accumulating because`, { mapKey, mapVal, sectorIsNull: !sector, sectorKeyIsNull: !sector[mapKey] })
      }
    }
  }

  setMapByIndex = (accessor: number[], update: MapLike<number | null>) => {
    if (this.getByIndex(...accessor)) {
      const sector = this.getByIndex(...accessor) as NestedMap<number | null>
      if (sector) {
        Object.keys(sector).forEach((key) => delete sector[key])
        debug && console.log(`setting sector ${accessor} with vals ${Object.values(sector).join('')} to ${Object.values(update).join('')}}`)
        Object.assign(sector, update)
      }
    }
  }

  abstract timeBucketToAccessor: (timeBucketAccessor: [number, MonthNumber, DayNumber, DayHour, MinuteNumber, MinuteNumber]) => number[]

  timestampToAccessor = (timestamp: number) => this.timeBucketToAccessor(timestampToYMDHMS(timestamp))

  setMapByTimestamp = (map: MapLike<number | null>, timestamp: number) => this.setMapByIndex(this.timestampToAccessor(timestamp), map)
  setKeyByTimestamp = (k: string, v: number | null, timestamp: number) => this.setKeyByIndex(k, v, ...this.timestampToAccessor(timestamp))

  accumulateKeyByTimestamp = (k: string, v: number | null, timestamp: number) => this.accumulateKeyByIndex(k, v, ...this.timestampToAccessor(timestamp))

  getByTimestamp = (timestamp: number) => this.getByIndex(...this.timestampToAccessor(timestamp)) as MapLike<number | null>

  /**
   * @function nullizeOverwritebles finds buckets exclusively between the last updated bucket, plus the to-be updated bucket if not the same as the last updatedBucket and makes contents of contained map null, so that count is effectively restarted on rollover
   * @param timestamp
   */
  nullizeOverwritebles = (timestamp: number) => {
    if (this.lastUpdatedTimestamp && this.arrayLengths) {
      const initialAccessor = this.timestampToAccessor(this.lastUpdatedTimestamp)
      const finalAccessor = this.timestampToAccessor(timestamp)

      if (!finalAccessor.every((v, i) => v === initialAccessor[i])) {
        const incrementBy = 1
        const arrayLengths = this.arrayLengths

        const nullableAccessors: number[][] = [...getAccessorsBetween({ initialAccessor, arrayLengths, incrementBy, finalAccessor }), finalAccessor]

        debug && console.log(`Nullifying ${nullableAccessors.length} becuase `, { initialAccessor, finalAccessor })

        nullableAccessors.forEach((accessor) => {
          const map = this.getByIndex(...accessor) as MapLike<number | null>
          const clone = { ...map }
          if (map && typeof map === 'object') {
            const keys = Object.keys(map)
            keys.forEach((k) => (map[k] = null))
            debug && console.log(`nullifiying`, { accessor, clone, map })
            this.setMapByIndex(accessor, map)
          }
        })
      }
    }
  }

  accumulateByTimeBucket = (measureValueMapRange: MeasureValueMapRange) => {
    const apportionmentRes: ApportionMeasureMapResponse = apportionMeasureValuesMap({ timeBucketDef: this.timeBucketDefinition, measureValueMapRange })
    debug && console.log(apportionmentRes)
    debug &&
      console.log(
        'apportionmentRes total',
        apportionmentRes.reduce((a, c) => (a += c.apportionedValuesMap.cantering ?? 0), 0),
      )

    apportionmentRes.forEach((apportionment) => {
      const valueIncrementMap = apportionment.apportionedValuesMap
      const bucketStart = apportionment.bucket[0]
      this.nullizeOverwritebles(bucketStart)
      Object.entries(valueIncrementMap).forEach(([k, v]) => this.accumulateKeyByTimestamp(k, v, bucketStart))
      this.lastUpdatedTimestamp = bucketStart
    })
  }

  toStateSnapShotsByAccessor = (initialAccessor: number[], finalAccessor: number[]): StateSnapshot<any>[] => {
    const stateSnapShots: StateSnapshot<any>[] = []
    if (this.arrayLengths) {
      const accessors = [
        initialAccessor,
        ...getAccessorsBetween({ initialAccessor, finalAccessor, arrayLengths: this.arrayLengths, incrementBy: 1 }),
        finalAccessor,
      ] as number[][]
      if (initialAccessor.every((v, i) => v === finalAccessor[i])) {
        accessors.shift()
      }

      accessors.filter(this.isValidAccessor).forEach((accessor) => {
        const state = this.getByIndex(...accessor) as MapLike<number | null>
        if (state && typeof state === 'object') {
          const stateSnapShot: StateSnapshot<any> = {
            timestamp: this.accessorToTimestamp(accessor),
            state,
          }
          stateSnapShots.push(stateSnapShot)
        }
      })
    }
    return stateSnapShots
  }

  toSnapShotByTimestampRange = ([startTimestamp, endTimeStamp]: [number, number]) => {
    // TODO if ouside this.latest - this number of days throw error
    const initialAccessor = this.timestampToAccessor(startTimestamp)
    const finalAccessor = this.timestampToAccessor(endTimeStamp)
    return this.toStateSnapShotsByAccessor(initialAccessor, finalAccessor)
  }

  abstract accessorToTimestamp: (accessor: number[]) => number
}
